<template>
  <div class="bui-entity-listing-page organization-editor-redesign">
    <div class="organization-editor-redesign__inner">
      <!-- Page header -->
      <header class="pe-header">
        <div class="pe-header__left">
          <div class="pe-header__title-row">
            <h1 class="bui-page-title">Organization Editor</h1>
            <button
              type="button"
              class="pe-header__refresh"
              aria-label="Refresh organization data"
              @click="loadOrganization()"
            >
              <PhArrowsClockwise :size="22" weight="bold" />
            </button>
          </div>
          <div class="pe-header__subtitle">{{ values.name }}</div>
        </div>
      </header>

      <!-- Organization Details -->
      <InfoCard>
        <template #title>
          <h2 class="info-card__title-text">Organization Details</h2>
        </template>

        <StatusBar :errorMessage="errorMessage" />

        <div class="oe-details-grid">
          <!-- TODO: think about adding below-field error message & error state indication (e.g. red border) [DSP-N26CxI4P] -->
          <div class="d-flex field-row">
            <label for="oe-name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
            <TextField id="oe-name" v-model="values.name" class="basis-1/2" width="100%" />
          </div>
          <div class="d-flex field-row">
            <label for="oe-bu" class="basis-1/2 text-sm font-bold">Business Unit #</label>
            <TextField id="oe-bu" v-model="values.businessUnitNumber" class="basis-1/2" width="100%" />
          </div>
        </div>
      </InfoCard>

      <!-- Locations -->
      <InfoCard flush>
        <template #title>
          <h2 class="info-card__title-text">Locations</h2>
        </template>
        <template #actions>
          <div class="oe-inline-dialog" v-if="organizationModel.locationSearch.results.length > 0">
            <CreateLocation :title="values.name" :timeZone="organizationModel.timezoneOptions"
              @save="addNewLocation($event)" :btnName="'Create New'" />
          </div>
        </template>

        <div class="oe-empty" v-if="organizationModel.locationSearch.results.length == 0">
          Locations are the physical locations used for media players —
          <span class="oe-inline-dialog oe-inline-dialog--link">
            <CreateLocation :title="values.name" :btnName="'create a new Location'" @save="addNewLocation($event)" :timeZone="organizationModel.timezoneOptions" />
          </span>
        </div>
        <div class="pe-table-wrap" v-if="organizationModel.locationSearch.results.length > 0">
          <table class="pe-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>State</th>
                <th>Country</th>
                <th>Region</th>
                <th class="pe-table__actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="location of organizationModel.locationSearch.results" :key="location.id">
                <td>
                  <a class="campaign-link" @click="editLocation(location.id)">{{ location.name }}</a>
                </td>
                <td>{{ location.city }}</td>
                <td>{{ location.state }}</td>
                <td>{{ location.country }}</td>
                <td>{{ location.region }}</td>
                <td class="pe-table__actions">
                  <div class="oe-row-actions">
                    <button type="button" class="pe-row-action-btn" aria-label="Edit location"
                      @click="editLocation(location.id)">
                      <PhPencilSimple :size="16" weight="bold" />
                    </button>
                    <DeleteDialog :showError="deleteError" :errorMsg="deleteErrorMsg" @close="closeDelete()" @save="deleteLocation(location.id)" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pe-table-footer" v-if="organizationModel.locationSearch.results.length > 0">
          Showing {{ organizationModel.locationSearch.results.length }} of {{ organizationModel.locationSearch.results.length }} location{{ organizationModel.locationSearch.results.length === 1 ? '' : 's' }}
        </div>
      </InfoCard>

      <!-- Child Organizations -->
      <InfoCard flush>
        <template #title>
          <h2 class="info-card__title-text">Child Organizations</h2>
        </template>
        <template #actions>
          <div class="oe-inline-dialog" v-if="organizationModel.subGroupSearch.results.length > 0">
            <CreateChildOrganization :btnName="'Create New'" :title="values.name" @save="addNewChild($event)" />
          </div>
        </template>

        <div class="oe-empty" v-if="organizationModel.subGroupSearch.results.length == 0">
          Organizations may contain child organizations they manage —
          <span class="oe-inline-dialog oe-inline-dialog--link">
            <CreateChildOrganization :title="values.name" :btnName="'create a Child Organization.'" @save="addNewChild($event)" />
          </span>
        </div>
        <div class="pe-table-wrap" v-if="organizationModel.subGroupSearch.results.length > 0">
          <table class="pe-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Locations</th>
                <th class="pe-table__actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="organization of organizationModel.subGroupSearch.results" :key="organization.id">
                <td>
                  <a class="campaign-link" @click="editOrganization(organization.id)">{{ organization.name }}</a>
                </td>
                <td>{{ organization.locations.length }}</td>
                <td class="pe-table__actions">
                  <div class="oe-row-actions">
                    <button type="button" class="pe-row-action-btn" aria-label="Edit organization"
                      @click="editOrganization(organization.id)">
                      <PhPencilSimple :size="16" weight="bold" />
                    </button>
                    <DeleteDialog @save="deleteChild(organization.id)" ref="deleteDialog" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pe-table-footer" v-if="organizationModel.subGroupSearch.results.length > 0">
          Showing {{ organizationModel.subGroupSearch.results.length }} of {{ organizationModel.subGroupSearch.results.length }} organization{{ organizationModel.subGroupSearch.results.length === 1 ? '' : 's' }}
        </div>
      </InfoCard>

      <!-- Sticky footer -->
      <footer class="editor-footer">
        <div class="editor-footer__inner">
          <ButtonCustomized label="Exit" v-if="!hasChanges" @click="onExit" ctaSecondary />
          <BeforeExitDialogue v-if="hasChanges" @save="submitAndExit" @exit="onExit" />
          <ButtonCustomized label="Save" cta @click="onSubmit" :disabled="!hasChanges" />
          <Saving ref="savingDialog" />
        </div>
      </footer>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { onBeforeMount, onMounted, onUnmounted, ref, watchEffect } from 'vue'
import { PhArrowsClockwise, PhPencilSimple } from '@phosphor-icons/vue';
import TextField from "@/components/form-fields/TextField.vue";
import ButtonCustomized from "@/components/form-fields/ButtonCustomized.vue";
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";
import useNotifications from "@/components/Notifications/composables/notifications";
import router from '@/router';
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import useOrganizationEditor from './OrganizationEditorHelper'
import DeleteDialog from '@/components/Dialog/DeleteDialog/DeleteDialog.vue'
import CreateLocation from './CreateLocation/CreateLocation.vue'
import { addChildOrganization, addLocation, submitDeleteOrganizationItem } from '@/helpers/api/organization/organization';
import  CreateChildOrganization from './CreateChildOrganization/CreateChildOrganization.vue';
import { submitDeleteLocationItem } from '@/helpers/api/location/location';
import BeforeExitDialogue from '@/components/Dialog/BeforeExitDialog/BeforeExitDialogue.vue'
import Saving from '@/components/Dialog/SavingDialog/Saving.vue'
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';
const savingDialog = ref()
const route = useRoute()
const notification = useNotifications();
 const deleteError = ref(false);
 const deleteErrorMsg = ref ('')
const {  values, submitForm, isSuccess, loadOrganization,
  hasChanges, errorMessage, removeBeforeLoad, isLoading, organizationId,organizationModel
} = useOrganizationEditor();
onBeforeMount(() => {
  organizationId.value = route.params.id as string
  loadOrganization()
})
onUnmounted(() => {
  removeBeforeLoad()
})
onBeforeRouteLeave((to, from) => {
  if (hasChanges.value) {
    const answer = window.confirm(
      'Changes you made may not be saved.'
    )
    if (!answer) return false
  }
})

async function submitAndExit() {
  savingDialog.value.open()
  await submitForm();
  savingDialog.value.close()
  if (isSuccess.value === true) {
    notification.success('Organization updated successfully!');
    removeBeforeLoad();
    router.push({ path: '/organizations' })
  }
}
async function onSubmit() {
 savingDialog.value.open()
  await submitForm();
  savingDialog.value.close()
  if (isSuccess.value === true) {
    removeBeforeLoad();
    notification.success('Organization updated successfully!');
  }
}
function onExit() {
  removeBeforeLoad()
  router.push({ path: '/organizations' })
}
async function addNewLocation (location:any) {
 await addLocation(organizationId.value , location);
 loadOrganization();
}
async function addNewChild (child :any){
    await addChildOrganization(organizationId.value , child);
    loadOrganization();
}
async function deleteChild(itemId:string ){
    await submitDeleteOrganizationItem(itemId);
    loadOrganization();
}
async function deleteLocation (itemId:string){
    await submitDeleteLocationItem(itemId).then(res =>
    {
        if (res.isSuccessful == false){
             deleteError.value = true ;
             deleteErrorMsg.value = res.messages[0]
        }else {
            loadOrganization();

        }
    })
}
function closeDelete (){
    deleteError.value = false ;
             deleteErrorMsg.value = ""
}
function editOrganization (itemId:string){
    organizationId.value =itemId
    loadOrganization()
   // router.push({ name: 'organization-editor', params: { id: itemId } });
    //location.reload()
}
function editLocation (itemId:string){
    router.push({ name: 'location-editor', params: { id: itemId } });

}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

/* Outer wrapper: paints the BUI page surface edge-to-edge (see the note in
   PlayerEditorView — `.bui-entity-listing-page` is wired in PageLayout.vue). */
.organization-editor-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.organization-editor-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

/* Details laid out two per row on wide screens. */
.oe-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 2rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.oe-empty {
  padding: 0 1.25rem 1rem;
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-size: 0.875rem;
}

.oe-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

/* Create dialogs' expander buttons/links hug content; links take tokens. */
.oe-inline-dialog {
  :deep(> .flex),
  :deep(.flex) {
    display: inline-flex;
    width: auto;
    flex: 0 0 auto;
  }

  &--link {
    display: inline-flex;

    :deep(a) {
      color: var(--bui-tbl-row-link, #4b1886);
      font-weight: 600;
      cursor: pointer;
    }
  }
}
</style>
